import React, { useMemo } from 'react';
import { Contact, ContactFilters } from '../../../config/api';
import ContactCard from './ContactCard';

interface ContactsCardViewProps {
  contacts: Contact[];
  loading: boolean;
  error: string | null;
  searchQuery: string;
  filters: ContactFilters;
  onContactClick: (contact: Contact) => void;
}

const ContactsCardView: React.FC<ContactsCardViewProps> = ({
  contacts,
  loading,
  error,
  searchQuery,
  filters,
  onContactClick
}) => {
  // Filter contacts
  const filteredContacts = useMemo(() => {
    return contacts.filter(contact => {
      // Search filter
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        const matchesSearch = 
          contact.name.toLowerCase().includes(searchLower) ||
          contact.email?.toLowerCase().includes(searchLower) ||
          contact.phone?.toLowerCase().includes(searchLower);
        
        if (!matchesSearch) return false;
      }

      // Status filter
      if (filters.status && contact.status !== filters.status) {
        return false;
      }

      // Type filter
      if (filters.contact_type && contact.contact_type !== filters.contact_type) {
        return false;
      }

      return true;
    });
  }, [contacts, searchQuery, filters]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lex-accent"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading contacts...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
        <p className="text-red-800 dark:text-red-200">{error}</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
      {filteredContacts.length === 0 ? (
        <div className="col-span-full text-center py-12">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8">
            <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No contacts found
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              {searchQuery || Object.keys(filters).length > 0 
                ? 'Try adjusting your search or filters'
                : 'Get started by adding your first contact'
              }
            </p>
          </div>
        </div>
      ) : (
        filteredContacts.map((contact) => (
          <div key={contact.id} className="w-full">
            <ContactCard
              contact={contact}
              onClick={() => onContactClick(contact)}
              searchQuery={searchQuery}
            />
          </div>
        ))
      )}
    </div>
  );
};

export default ContactsCardView; 